import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"
import Grid from "@mui/material/Grid"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import error_mark from "assets/images/error_mark.svg"
import MultiSelectDropdown from "components/MultiSelectDropdown/MultiSelectDropdown"
import SearchInputV2 from "components/SearchInputV2/SearchInputV2"
import { Countries } from "enums/dropdownValues"
import { useCallback, useEffect, useMemo, useRef } from "react"
import useSites, { SiteOption } from "./useSites"

type SiteType = "destination" | "source"

interface SitesProps {
    type: SiteType
    selectedSiteId: number | null
    onSiteSelect: (site: SiteOption) => void
    hasError?: boolean
    /** Pins warehouses to the top of the list. Defaults to the deal template's `warehouse_first` rule. */
    warehouseFirst?: boolean
    pinnedSiteIds?: number[]
    defaultSiteId?: number
}

const Sites = ({
    type,
    selectedSiteId,
    onSiteSelect,
    hasError = false,
    warehouseFirst,
    pinnedSiteIds = [],
    defaultSiteId,
}: SitesProps) => {
    const { states, functions } = useSites(warehouseFirst)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const hasAutoSelectedDefault = useRef(false)

    const handleSiteChange = (siteId: number) => {
        const site = functions.getSiteById(siteId)
        if (site) {
            onSiteSelect(site)
        }
    }

    const sortedSites = useMemo(() => {
        let sites = [...states.displayedSites]

        if (pinnedSiteIds.length > 0) {
            sites.sort((a, b) => {
                const aIsPinned = pinnedSiteIds.includes(a.id)
                const bIsPinned = pinnedSiteIds.includes(b.id)

                if (aIsPinned && !bIsPinned) return -1
                if (!aIsPinned && bIsPinned) return 1

                return 0
            })
        }

        return sites
    }, [states.displayedSites, pinnedSiteIds])

    useEffect(() => {
        if (defaultSiteId && !selectedSiteId && !hasAutoSelectedDefault.current && states.displayedSites.length > 0) {
            const defaultSite = states.displayedSites.find(site => site.id === defaultSiteId)
            if (defaultSite) {
                hasAutoSelectedDefault.current = true
                onSiteSelect({
                    id: defaultSite.id,
                    name: defaultSite.name,
                    site_id: defaultSite.site_id,
                    pincode: defaultSite.pincode,
                    address: defaultSite.address,
                    state: defaultSite.state,
                    state_id: defaultSite.state_id,
                    city: defaultSite.city,
                    city_id: defaultSite.city_id,
                    country_id: defaultSite.country_id,
                    latitude: defaultSite.latitude,
                    longitude: defaultSite.longitude,
                })
            }
        }
    }, [defaultSiteId, selectedSiteId, states.displayedSites, onSiteSelect])

    // Infinite scroll: loads more sites when user scrolls within 50px of the bottom
    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container || !states.hasMoreSites || states.isLoadingMore) return

        const { scrollTop, scrollHeight, clientHeight } = container
        if (scrollHeight - scrollTop - clientHeight < 50) {
            functions.handleShowMore()
        }
    }, [states.hasMoreSites, states.isLoadingMore, functions])

    const title = type === "destination" ? "Select Destination Site" : "Select Source Site"

    return (
        <Box
            sx={{
                border: hasError ? "1px solid #F94054" : "1px solid #E5E7EB",
                borderRadius: "8px",
                bgcolor: "#FAFAFA",
                p: 2,
            }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Typography fontSize="14px" fontWeight={500} color="#101941">
                    {title}
                </Typography>
                {hasError && <img src={error_mark} alt="error" width={14} height={14} />}
            </Box>

            <Box mb={2}>
                <Grid container spacing={1.5} alignItems="center">
                    <Grid size="auto">
                        <Box width={180}>
                            <SearchInputV2
                                placeholder="Search by Site Name/ID"
                                onSearch={functions.handleSearchSiteName}
                                value={states.filters.search_site_name}
                                searchIcon={true}
                            />
                        </Box>
                    </Grid>
                    <Grid size="auto">
                        <Box width={150}>
                            <SearchInputV2
                                placeholder="Search by Pincode"
                                onSearch={functions.handleSearchPincode}
                                value={states.filters.search_pincode}
                                type="number"
                                maxCharacter={6}
                                searchIcon={true}
                            />
                        </Box>
                    </Grid>
                    <Grid size="auto">
                        <Box width={120}>
                            <MultiSelectDropdown
                                title="Country"
                                options={Countries?.map((country: any) => ({
                                    id: country?.id,
                                    title: country?.title,
                                }))}
                                id={`${type}_selectedCountry`}
                                selectionType="single"
                                onChange={() => {}}
                                selectedOptions={
                                    states.filters.selectedCountry
                                        ? [
                                              {
                                                  id: states.filters.selectedCountry.id,
                                                  title: states.filters.selectedCountry.title,
                                              },
                                          ]
                                        : []
                                }
                                onSelect={(value: any) => functions.handleCountryChange(value)}
                            />
                        </Box>
                    </Grid>
                    <Grid size="auto">
                        <Box width={120}>
                            <MultiSelectDropdown
                                title="State"
                                options={states.statesOptions}
                                id={`${type}_states`}
                                onChange={() => {}}
                                selectedOptions={states.filters.selectedState || []}
                                onSelect={(value: any) => functions.handleStateChange(value)}
                            />
                        </Box>
                    </Grid>
                    <Grid size="auto">
                        <Box width={120}>
                            <MultiSelectDropdown
                                title="City"
                                options={states.citiesOptions}
                                id={`${type}_cities`}
                                onChange={() => {}}
                                selectedOptions={states.filters.selectedCity || []}
                                onSelect={(value: any) => functions.handleCityChange(value)}
                                disabled={states.filters.selectedState?.length === 0}
                            />
                        </Box>
                    </Grid>
                    <Grid size="auto">
                        <Box width={120}>
                            <MultiSelectDropdown
                                title="Added By"
                                options={states.addedByOptions}
                                onChange={() => {}}
                                id={`${type}_addedBy`}
                                selectedOptions={states.filters.addedBy || []}
                                onSelect={(value: any) => functions.handleAddedByChange(value)}
                            />
                        </Box>
                    </Grid>
                    <Grid size="auto">
                        <Typography
                            onClick={functions.handleResetFilters}
                            sx={{
                                color: "#FC441E",
                                fontWeight: 500,
                                fontSize: "12px",
                                cursor: "pointer",
                                px: 1,
                                "&:hover": {
                                    opacity: 0.8,
                                },
                            }}>
                            Reset
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {states.isSitesLoading && states.displayedSites.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <CircularProgress size={40} sx={{ color: "#FC441E" }} />
                </Box>
            ) : states.displayedSites.length === 0 ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={100}
                    color="#6B7280"
                    fontSize="14px">
                    No sites found matching your criteria
                </Box>
            ) : (
                <>
                    <Box
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                        sx={{
                            maxHeight: 280,
                            overflowY: "auto",
                            p: 0.5,
                            "&::-webkit-scrollbar": {
                                width: "6px",
                            },
                            "&::-webkit-scrollbar-track": {
                                background: "#f1f1f1",
                                borderRadius: "3px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                                background: "#c1c1c1",
                                borderRadius: "3px",
                                "&:hover": {
                                    background: "#a1a1a1",
                                },
                            },
                        }}>
                        <Grid container spacing={1.5}>
                            {sortedSites.map((site: any) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={site.id}>
                                    <Box
                                        component="label"
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        p={1}
                                        bgcolor={selectedSiteId === site.id ? "#FFF5F3" : "#fff"}
                                        border={selectedSiteId === site.id ? "1px solid #FC441E" : "1px solid #E5E7EB"}
                                        borderRadius="6px"
                                        sx={{
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                borderColor: "#FC441E",
                                                bgcolor: "#FFF5F3",
                                            },
                                        }}>
                                        <Box
                                            component="input"
                                            type="radio"
                                            name={`${type}_site`}
                                            value={site.id}
                                            checked={selectedSiteId === site.id}
                                            onChange={() => handleSiteChange(site.id)}
                                            sx={{
                                                appearance: "none",
                                                width: 16,
                                                height: 16,
                                                border:
                                                    selectedSiteId === site.id
                                                        ? "2px solid #FC441E"
                                                        : "2px solid #B7BAC6",
                                                borderRadius: "50%",
                                                cursor: "pointer",
                                                flexShrink: 0,
                                                position: "relative",
                                                "&:checked::after": {
                                                    content: '""',
                                                    position: "absolute",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    width: 8,
                                                    height: 8,
                                                    bgcolor: "#FC441E",
                                                    borderRadius: "50%",
                                                },
                                            }}
                                        />
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                            sx={{
                                                overflow: "hidden",
                                                flex: 1,
                                            }}>
                                            <Typography
                                                fontSize="13px"
                                                fontWeight={400}
                                                color="#101941"
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                                title={site.name}>
                                                {site.name}
                                            </Typography>
                                            {site.is_warehouse === 1 && (
                                                <Tooltip title="This is a warehouse">
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        fontSize={"10px"}
                                                        justifyContent="center"
                                                        height={"20px"}
                                                        width={"20px"}
                                                        sx={{
                                                            flexShrink: 0,
                                                            borderRadius: "50%",
                                                            bgcolor: "#E5E7EB",
                                                            color: "#70758D",
                                                        }}>
                                                        W
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        {states.isLoadingMore && (
                            <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                                <CircularProgress size={24} sx={{ color: "#FC441E" }} />
                            </Box>
                        )}
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        mt={1.5}
                        pt={1}
                        borderTop="1px solid #E5E7EB">
                        <Typography fontSize="12px" variant="label" fontWeight={300} color="#6B7280">
                            Showing {states.displayedSites.length} of {states.totalSitesCount} sites
                        </Typography>
                    </Box>
                </>
            )}
        </Box>
    )
}

export default Sites
